import { supabase } from '../lib/supabase';
import { getActiveSeason } from '../config/seasons';
import { getAgeDivision, isMinor } from '../config/categories';

// Age divisions live in src/config/categories.js. Pass the season year
// explicitly when rendering a past roster — a 2026 student must not be
// re-binned against the 2027 cutoff.
export { getAgeDivision } from '../config/categories';

// =====================================================
// AGE DIVISION HELPERS
// =====================================================

export const isStudentMinor = (dateOfBirth, year = getActiveSeason().year) => {
  if (!dateOfBirth) return false;
  return isMinor(dateOfBirth, year);
};

// =====================================================
// SCHOOL (persistent identity)
// =====================================================

/**
 * The school this user belongs to, or null. Goes through school_members, so
 * it keeps working unchanged if a school ever gets a second teacher.
 */
export const getMySchool = async (userId) => {
  const { data, error } = await supabase
    .from('school_members')
    .select('role, schools (*)')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;
  if (!data?.schools) return null;
  return { ...data.schools, my_role: data.role };
};

/**
 * Create the school. A DB trigger makes the creator its owner in the same
 * statement, so there is no window where a school exists with no members.
 */
export const createSchool = async (schoolData) => {
  const { data, error } = await supabase
    .from('schools')
    .insert(schoolData)
    .select()
    .single();

  if (error) {
    // Raised by schools_add_owner_member() when this account already belongs
    // to a school. Surfaced as-is: the message is written to be shown.
    if (error.code === '23505') {
      throw new Error(error.message || 'This account is already linked to a school.');
    }
    throw error;
  }
  return data;
};

export const updateSchool = async (schoolId, schoolData) => {
  const { data, error } = await supabase
    .from('schools')
    .update({ ...schoolData, updated_at: new Date().toISOString() })
    .eq('id', schoolId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// =====================================================
// SCHOOL SEASONS (one row per year the school takes part)
// =====================================================

export const getSchoolSeasons = async (schoolId) => {
  const { data, error } = await supabase
    .from('school_seasons')
    .select('*')
    .eq('school_id', schoolId)
    .order('competition_year', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const getSchoolSeason = async (schoolId, year) => {
  const { data, error } = await supabase
    .from('school_seasons')
    .select('*')
    .eq('school_id', schoolId)
    .eq('competition_year', year)
    .maybeSingle();

  if (error) throw error;
  return data;
};

/**
 * Open (or re-sign) a season. The waiver is signed on behalf of "my school and
 * all registered students", and the roster changes every year, so a fresh row
 * with a fresh signature is required for each season — a 2026 signature does
 * not cover a 2027 student.
 */
export const signSeasonWaiver = async (schoolId, year, waiver) => {
  const existing = await getSchoolSeason(schoolId, year);

  const payload = {
    waiver_agreed: true,
    waiver_agreed_at: new Date().toISOString(),
    waiver_signer_name: waiver.waiver_signer_name,
  };

  if (existing) {
    const { data, error } = await supabase
      .from('school_seasons')
      .update({ ...payload, updated_at: new Date().toISOString() })
      .eq('id', existing.id)
      .select()
      .single();
    if (error) throw error;
    return data;
  }

  const { data, error } = await supabase
    .from('school_seasons')
    .insert({ school_id: schoolId, competition_year: year, ...payload })
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Finalizing goes through /api/ensemble (schoolFinalize), which checks the
// roster and records the billing snapshot; the browser only re-opens.
export const reopenSeason = async (seasonId) => {
  const { data, error } = await supabase
    .from('school_seasons')
    .update({ is_finalized: false, finalized_at: null })
    .eq('id', seasonId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// =====================================================
// STUDENT (REGISTRATION) OPERATIONS
// =====================================================

/**
 * Create a new minimal student registration row against one season.
 * Uses the teacher's userId so the plain-ownership RLS path works too.
 */
export const createStudentRegistration = async (seasonId, teacherUserId, year) => {
  const { data, error } = await supabase
    .from('registrations')
    .insert({
      user_id: teacherUserId,
      registration_type: 'school',
      competition_year: year,
      status: 'draft',
      current_step: 1,
      school_season_id: seasonId,
      parent_consent_status: 'not_required'
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Details carried over from an earlier season ("Add from last year"): the
// ones that don't change year to year. Level, training, experience, medical,
// solos, music and consent are left for this season.
export const CARRY_OVER_FIELDS = [
  'first_name', 'last_name', 'name_pronunciation', 'date_of_birth', 'gender',
  'email', 'phone_number', 'address_street', 'address_city', 'address_province',
  'address_postal_code', 'address_country', 'parent_guardian_name', 'parent_guardian_email',
  'emergency_contact_name', 'emergency_contact_relationship', 'emergency_contact_phone',
  'emergency_contact_email',
];

/**
 * Students from this school's earlier seasons, one per dancer (matched by
 * name + birthday, most recent season wins), newest season first.
 * Returns [{ year, dancerInfo }].
 */
export const getPastSeasonStudents = async (schoolId, beforeYear) => {
  const { data: seasons, error } = await supabase
    .from('school_seasons')
    .select('id, competition_year')
    .eq('school_id', schoolId)
    .lt('competition_year', beforeYear);
  if (error) throw error;
  if (!seasons?.length) return [];

  const yearBySeason = Object.fromEntries(seasons.map(s => [s.id, s.competition_year]));
  const { data: rows, error: rowsError } = await supabase
    .from('registrations')
    .select('school_season_id, dancer_information ( * )')
    .in('school_season_id', seasons.map(s => s.id))
    .eq('registration_type', 'school');
  if (rowsError) throw rowsError;

  const byDancer = new Map();
  (rows || []).forEach(r => {
    const di = Array.isArray(r.dancer_information) ? r.dancer_information[0] : r.dancer_information;
    if (!di?.first_name) return;
    const key = studentKey(di);
    const year = yearBySeason[r.school_season_id];
    if (!byDancer.has(key) || byDancer.get(key).year < year) byDancer.set(key, { year, dancerInfo: di });
  });
  return [...byDancer.values()].sort((a, b) =>
    b.year - a.year || `${a.dancerInfo.first_name} ${a.dancerInfo.last_name}`.localeCompare(`${b.dancerInfo.first_name} ${b.dancerInfo.last_name}`));
};

/** Same dancer across seasons: name (case/spacing-insensitive) + birthday. */
export const studentKey = (di) =>
  `${String(di?.first_name || '').trim().toLowerCase()} ${String(di?.last_name || '').trim().toLowerCase()}|${String(di?.date_of_birth || '').slice(0, 10)}`;

/** Copy a past season's details into a new student's form data. */
export const carryOverDetails = (pastDancerInfo) =>
  Object.fromEntries(CARRY_OVER_FIELDS.filter(f => pastDancerInfo[f]).map(f => [f, pastDancerInfo[f]]));

/**
 * All student registrations for one season, joined with dancer_information.
 */
export const getSeasonStudents = async (seasonId) => {
  const { data, error } = await supabase
    .from('registrations')
    .select(`
      id,
      status,
      parent_consent_status,
      created_at,
      dancer_information (
        id,
        first_name,
        last_name,
        name_pronunciation,
        date_of_birth,
        gender,
        email,
        phone_number,
        address_street,
        address_city,
        address_province,
        address_postal_code,
        address_country,
        years_training,
        current_studio,
        competition_experience,
        current_level,
        emergency_contact_name,
        emergency_contact_relationship,
        emergency_contact_phone,
        emergency_contact_email,
        medical_conditions,
        special_accommodations,
        parent_guardian_name,
        parent_guardian_email
      )
    `)
    .eq('school_season_id', seasonId)
    .eq('registration_type', 'school')
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data || [];
};

/**
 * Where a student's consent status should sit, given what we now know about
 * them. Never downgrades a status that represents real-world progress: once a
 * parent has actually consented, editing the student can't undo that.
 */
export const nextConsentStatus = (isMinorStudent, parentEmail, current) => {
  if (!isMinorStudent) return 'not_required';
  if (current === 'received') return 'received';
  if (current === 'pending' && parentEmail) return 'pending';
  return 'required';
};

/**
 * Save (upsert) dancer information for a student.
 *
 * Also persists parent_consent_status. This used to be an UPDATE with an empty
 * object, so the status never left 'not_required' and every minor's missing
 * consent was invisible — see database/2026-09-18-parent-consent-required-state.sql
 */
export const saveStudentInfo = async (registrationId, studentData, year) => {
  const ageDivision = getAgeDivision(studentData.date_of_birth, year);
  const minor = isStudentMinor(studentData.date_of_birth, year);

  const payload = { ...studentData };
  delete payload.age_division; // derived, not stored on dancer_information

  const { data: existing } = await supabase
    .from('dancer_information')
    .select('id')
    .eq('registration_id', registrationId)
    .maybeSingle();

  let saved;
  if (existing) {
    const { data, error } = await supabase
      .from('dancer_information')
      .update(payload)
      .eq('registration_id', registrationId)
      .select()
      .single();
    if (error) throw error;
    saved = data;
  } else {
    const { data, error } = await supabase
      .from('dancer_information')
      .insert({ registration_id: registrationId, ...payload })
      .select()
      .single();
    if (error) throw error;
    saved = data;
  }

  const { data: reg } = await supabase
    .from('registrations')
    .select('parent_consent_status')
    .eq('id', registrationId)
    .maybeSingle();

  const consentStatus = nextConsentStatus(
    minor,
    studentData.parent_guardian_email,
    reg?.parent_consent_status
  );

  if (consentStatus !== reg?.parent_consent_status) {
    const { error: consentError } = await supabase
      .from('registrations')
      .update({ parent_consent_status: consentStatus })
      .eq('id', registrationId);
    if (consentError) throw consentError;
  }

  return {
    ...saved,
    age_division: ageDivision,
    is_minor: minor,
    parent_consent_status: consentStatus,
  };
};

/**
 * Delete a student registration (cascades to dancer_information and variations).
 */
export const deleteStudentRegistration = async (registrationId) => {
  const { error } = await supabase
    .from('registrations')
    .delete()
    .eq('id', registrationId)
    .eq('status', 'draft');

  if (error) throw error;
};

// =====================================================
// VARIATION OPERATIONS (reuses existing variations table)
// =====================================================

export const getStudentVariations = async (registrationId) => {
  const { data, error } = await supabase
    .from('variations')
    .select('*')
    .eq('registration_id', registrationId)
    .order('variation_number');

  if (error) throw error;
  return data || [];
};

// Fields with DB CHECK constraints that reject empty string — coerce '' → null
const CONSTRAINED_VARIATION_FIELDS = ['start_music', 'footwear'];

const sanitizeVariationPayload = (variationData) => {
  const out = { ...variationData };
  CONSTRAINED_VARIATION_FIELDS.forEach(f => {
    if (out[f] === '') out[f] = null;
  });
  return out;
};

export const saveStudentVariation = async (registrationId, variationNumber, variationData) => {
  const payload = sanitizeVariationPayload(variationData);

  const { data: existing } = await supabase
    .from('variations')
    .select('id')
    .eq('registration_id', registrationId)
    .eq('variation_number', variationNumber)
    .maybeSingle();

  if (existing) {
    const { data, error } = await supabase
      .from('variations')
      .update(payload)
      .eq('registration_id', registrationId)
      .eq('variation_number', variationNumber)
      .select()
      .single();
    if (error) throw error;
    return data;
  } else {
    const { data, error } = await supabase
      .from('variations')
      .insert({ registration_id: registrationId, variation_number: variationNumber, ...payload })
      .select()
      .single();
    if (error) throw error;
    return data;
  }
};

export const deleteStudentVariation = async (registrationId, variationNumber) => {
  const { error } = await supabase
    .from('variations')
    .delete()
    .eq('registration_id', registrationId)
    .eq('variation_number', variationNumber);

  if (error) throw error;
};

// =====================================================
// PARENT CONSENT TOKEN OPERATIONS
// =====================================================

export const getOrCreateConsentToken = async (registrationId, parentEmail) => {
  const { data: existing } = await supabase
    .from('parent_consent_tokens')
    .select('*')
    .eq('registration_id', registrationId)
    .maybeSingle();

  if (existing) {
    // If parent email changed, update it
    if (existing.parent_email !== parentEmail) {
      const { data, error } = await supabase
        .from('parent_consent_tokens')
        .update({ parent_email: parentEmail, consent_given: false, consented_at: null })
        .eq('registration_id', registrationId)
        .select()
        .single();
      if (error) throw error;
      return data;
    }
    return existing;
  }

  const { data, error } = await supabase
    .from('parent_consent_tokens')
    .insert({ registration_id: registrationId, parent_email: parentEmail })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getConsentToken = async (registrationId) => {
  const { data, error } = await supabase
    .from('parent_consent_tokens')
    .select('*')
    .eq('registration_id', registrationId)
    .maybeSingle();

  if (error) throw error;
  return data;
};
